import React, { useState, useEffect } from 'react';
import { X, Home, Search } from 'lucide-react';
import { createProperty } from '../services/propertyApi';
import type { Property } from '../services/propertyApi';
import { fetchAddressByCep, detectPostalCodeType } from '../services/cepApi';
import { useI18n } from '../contexts/I18nContext';

interface QuickCreatePropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (property: Property) => void;
  clientId: string;
  clientName: string;
  initialName?: string;
}

const QuickCreatePropertyModal: React.FC<QuickCreatePropertyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  clientName,
  initialName = ''
}) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: initialName,
    address: '',
    zipCode: '',
    city: '',
    state: '',
    propertyType: 'residential' as 'residential' | 'commercial' | 'other',
    price: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState<string>('');

  // Atualizar nome quando initialName mudar ou modal abrir
  useEffect(() => {
    if (isOpen && initialName) {
      setFormData(prev => ({ ...prev, name: initialName }));
    }
  }, [isOpen, initialName]);

  // Resetar formulário quando modal fechar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        address: '',
        zipCode: '',
        city: '',
        state: '',
        propertyType: 'residential',
        price: ''
      });
      setError('');
      setCepError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validar client_id antes de enviar
      if (!clientId || clientId.trim() === '') {
        throw new Error(t('quickCreate.property.clientNotSelected'));
      }

      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue <= 0) {
        throw new Error(t('quickCreate.property.price') + ' ' + t('quickCreate.property.required'));
      }

      const response = await createProperty({
        client_id: clientId,
        name: formData.name.trim(),
        address: formData.address.trim(),
        zip_code: formData.zipCode.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state.trim() || undefined,
        property_type: formData.propertyType,
        price: priceValue,
        special_instructions: undefined
      });

      // A resposta da API já vem como { property: {...} } em response.data
      const createdProperty = response.property;
      if (!createdProperty) {
        console.error('Resposta da API:', response);
        throw new Error(t('quickCreate.property.invalidResponse'));
      }
      onSuccess(createdProperty);

      // O formulário será limpo pelo useEffect quando o modal fechar
      onClose();
    } catch (err: any) {
      console.error('Erro ao criar propriedade:', err);
      console.error('Erro completo:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      
      let errorMessage = t('quickCreate.property.error');
      
      if (err.response) {
        // Erro com resposta do servidor
        if (err.response.status === 404) {
          errorMessage = t('quickCreate.property.error.endpoint');
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.error || t('quickCreate.property.error.invalidData');
        } else if (err.response.status === 500) {
          errorMessage = err.response.data?.error || t('quickCreate.property.error.server');
        } else {
          errorMessage = err.response.data?.error || err.message || t('quickCreate.property.error');
        }
      } else if (err.request) {
        // Erro de rede (sem resposta)
        errorMessage = t('quickCreate.property.error.connection');
      } else {
        // Outro tipo de erro
        errorMessage = err.message || t('quickCreate.property.error');
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para buscar endereço por CEP
  const handleCepChange = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');
    setCepError('');
    
    // Atualizar zipCode imediatamente (mantém formatação visual)
    setFormData(prev => ({ ...prev, zipCode: cep }));
    
    const detectedType = detectPostalCodeType(cleanCep);
    console.log('🔍 CEP digitado:', cep, 'Limpo:', cleanCep, 'Tipo:', detectedType, 'Tamanho:', cleanCep.length);
    
    // Só busca se tiver o tamanho mínimo (8 para CEP, 5 para ZIP)
    if ((detectedType === 'BR' && cleanCep.length === 8) || 
        (detectedType === 'US' && cleanCep.length >= 5)) {
      setLoadingCep(true);
      try {
        console.log('🔍 Buscando endereço para CEP:', cleanCep, 'Tipo:', detectedType);
        const addressData = await fetchAddressByCep(cleanCep);
        console.log('✅ Endereço encontrado:', addressData);
        // Usar setState funcional para garantir que usa o estado mais recente
        setFormData(prev => ({
          ...prev,
          zipCode: addressData.zip_code,
          address: addressData.address || prev.address, // Preserva se já tinha algo digitado
          city: addressData.city,
          state: addressData.state
        }));
        console.log('✅ Campos atualizados com sucesso');
      } catch (err: any) {
        console.error('❌ Erro ao buscar CEP:', err);
        setCepError(err.message || t('quickCreate.property.cepNotFound'));
        // Não limpa os campos se o CEP não for encontrado
      } finally {
        setLoadingCep(false);
      }
    } else if (cleanCep.length > 0) {
      console.log('⚠️ CEP incompleto ou formato inválido:', cleanCep);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Home size={20} color="#9333ea" />
            <h3 style={{ margin: 0, color: '#333' }}>{t('quickCreate.property.title')}</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} color="#666" />
          </button>
        </div>

        <div style={{
          backgroundColor: '#f3f4f6',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#666'
        }}>
          {t('quickCreate.property.client')} <strong>{clientName}</strong>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              {t('quickCreate.property.name')} * <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('quickCreate.property.required')}</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder={t('quickCreate.property.exampleName')}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              {t('quickCreate.property.address')} * <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('quickCreate.property.required')}</span>
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              placeholder={t('quickCreate.property.exampleAddress')}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                {t('quickCreate.property.zipCode')}
                {loadingCep && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>{t('quickCreate.property.searchingCep')}</span>}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => {
                    // Formatar CEP enquanto digita (adicionar hífen)
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length > 5 && value.length <= 8) {
                      value = value.substring(0, 5) + '-' + value.substring(5);
                    }
                    handleCepChange(value);
                  }}
                  onBlur={(e) => {
                    // Tenta buscar se o campo perder o foco e tiver valor
                    const cleanCep = e.target.value.replace(/\D/g, '');
                    if (cleanCep.length === 8 && !loadingCep) {
                      // Força busca se tiver 8 dígitos (CEP completo)
                      handleCepChange(e.target.value);
                    }
                  }}
                  placeholder="00000-000 (CEP) ou 00000 (ZIP)"
                  maxLength={9}
                  style={{
                    width: '100%',
                    padding: '8px',
                    paddingRight: loadingCep ? '35px' : '8px',
                    border: cepError ? '1px solid #dc2626' : '1px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none',
                    fontSize: '14px'
                  }}
                />
                {loadingCep && (
                  <Search 
                    size={16} 
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9333ea',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}
                  />
                )}
              </div>
              {cepError && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                  {cepError}
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                {t('quickCreate.property.cepInstructions')}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                {t('quickCreate.property.type')}
              </label>
              <select
                value={formData.propertyType}
                onChange={(e) => setFormData({ ...formData, propertyType: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px'
                }}
              >
                <option value="residential">{t('properties.type.residential')}</option>
                <option value="commercial">{t('properties.type.commercial')}</option>
                <option value="other">{t('properties.type.other')}</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                {t('quickCreate.property.city')}
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder={t('quickCreate.property.autoFill')}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                {t('quickCreate.property.state')}
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                placeholder={t('quickCreate.property.autoFill')}
                maxLength={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  textTransform: 'uppercase'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              {t('quickCreate.property.price')} * <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('quickCreate.property.required')}</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder={t('quickCreate.property.examplePrice')}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.address.trim() || !formData.price.trim()}
              style={{
                flex: 1,
                backgroundColor: '#9333ea',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading || !formData.name.trim() || !formData.address.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading || !formData.name.trim() || !formData.address.trim() ? 0.6 : 1
              }}
            >
              {isLoading ? t('quickCreate.property.registering') : t('quickCreate.property.register')}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                flex: 1,
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {t('actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCreatePropertyModal;

